import { useCallback, useEffect, useRef, useState } from 'react';
import { App } from '@capacitor/app';
import { Quat, quatFromEuler, quatMean } from '../lib/quaternion';

export type SensorPermission =
  | 'unknown'
  | 'requesting'
  | 'granted'
  | 'denied'
  | 'unavailable';

export interface Orientation {
  alpha: number;
  beta: number;
  gamma: number;
  /** Cached unit quaternion derived from (alpha, beta, gamma). */
  q: Quat;
  /** monotonic timestamp (ms) when this sample arrived */
  t: number;
}

/* iOS-specific: the static `requestPermission` on DeviceOrientationEvent is
   non-standard, so we type it ourselves. */
interface IOSDeviceOrientationEventCtor {
  requestPermission?: () => Promise<'granted' | 'denied'>;
}

/**
 * Subscribe to DeviceOrientation events with iOS-aware permission handling
 * and a captureSample() helper that averages over a short window for stability.
 *
 * IMPORTANT: `request()` MUST be called from a synchronous user-gesture
 * handler (button onClick) on iOS, otherwise the system silently denies.
 */
export function useDeviceOrientation() {
  const [permission, setPermission] = useState<SensorPermission>('unknown');
  const [orientation, setOrientation] = useState<Orientation | null>(null);
  const subscribedRef = useRef(false);

  // Latest sample, kept in a ref so captureSample can read it without re-renders.
  const latestRef = useRef<Orientation | null>(null);
  // Subscribers used by captureSample to collect a window of samples.
  const collectorsRef = useRef<Set<(o: Orientation) => void>>(new Set());

  const handler = useCallback((e: DeviceOrientationEvent) => {
    if (e.alpha == null || e.beta == null || e.gamma == null) return;
    const o: Orientation = {
      alpha: e.alpha,
      beta: e.beta,
      gamma: e.gamma,
      q: quatFromEuler(e.alpha, e.beta, e.gamma),
      t: performance.now(),
    };
    latestRef.current = o;
    setOrientation(o);
    collectorsRef.current.forEach((fn) => fn(o));
  }, []);

  const subscribe = useCallback(() => {
    if (subscribedRef.current) return;
    window.addEventListener('deviceorientation', handler, true);
    subscribedRef.current = true;
  }, [handler]);

  const unsubscribe = useCallback(() => {
    window.removeEventListener('deviceorientation', handler, true);
    subscribedRef.current = false;
  }, [handler]);

  const request = useCallback(async () => {
    setPermission('requesting');
    try {
      // Detect availability
      if (typeof DeviceOrientationEvent === 'undefined') {
        setPermission('unavailable');
        return;
      }
      const Ctor = DeviceOrientationEvent as unknown as IOSDeviceOrientationEventCtor;
      if (typeof Ctor.requestPermission === 'function') {
        const state = await Ctor.requestPermission();
        if (state === 'granted') {
          setPermission('granted');
          subscribe();
        } else {
          setPermission('denied');
        }
        return;
      }
      // Non-iOS — no explicit permission, just subscribe.
      setPermission('granted');
      subscribe();
    } catch (err) {
      console.error('Sensor permission error', err);
      setPermission('denied');
    }
  }, [subscribe]);

  useEffect(() => () => unsubscribe(), [unsubscribe]);

  /* ── (A) App lifecycle — re-attach the listener when the app returns
       from background.

       iOS pauses the DeviceOrientation event stream while a WKWebView is
       suspended (Home swipe, incoming call, switching apps, screen lock).
       When the app resumes, the existing event listener is still attached
       but iOS doesn't always restart the stream — the HUD freezes on the
       last sample seen before suspension.

       Detaching + re-attaching the listener forces iOS to spin up a
       fresh DeviceMotion session. We use Capacitor's `appStateChange`
       event (more reliable than `document.visibilitychange` inside
       WKWebView). The web PWA build still works — @capacitor/app's web
       fallback is a no-op for addListener, so the bounce never fires
       there. ─── */
  useEffect(() => {
    let detachAppListener: (() => Promise<void>) | undefined;

    void App.addListener('appStateChange', ({ isActive }) => {
      if (!isActive || !subscribedRef.current) return;
      // Force a fresh sensor session.
      window.removeEventListener('deviceorientation', handler, true);
      window.addEventListener('deviceorientation', handler, true);
      console.log('[sensors] App resumed — refreshed DeviceOrientation listener');
    }).then((h) => {
      detachAppListener = () => h.remove();
    });

    return () => {
      void detachAppListener?.();
    };
  }, [handler]);

  /* ── (B) Cold-start heartbeat — detect stalled sensor streams.

       On some iOS 17/18 versions `requestPermission()` returns 'granted'
       but DeviceOrientation never delivers its first event until the
       user shakes the phone. We give it 1.5 s; if still no sample, we
       bounce the listener once (cheap, fully passive — no permission
       prompt). One retry is enough in practice. ─── */
  useEffect(() => {
    if (permission !== 'granted') return;

    const grantAt = performance.now();
    const tid = window.setTimeout(() => {
      if (!subscribedRef.current) return;
      const latest = latestRef.current;
      if (!latest || latest.t < grantAt) {
        window.removeEventListener('deviceorientation', handler, true);
        window.addEventListener('deviceorientation', handler, true);
        console.log('[sensors] No samples 1.5s after grant — refreshed listener');
      }
    }, 1500);

    return () => window.clearTimeout(tid);
  }, [permission, handler]);

  /**
   * Collect samples for `durationMs` and return their averaged orientation.
   * 800 ms is a reasonable default — long enough to smooth high-frequency
   * jitter, short enough that the user doesn't have to hold still forever.
   */
  const captureSample = useCallback(
    (durationMs = 800): Promise<Orientation> => {
      return new Promise((resolve, reject) => {
        if (permission !== 'granted') {
          reject(new Error('Sensor permission not granted'));
          return;
        }
        const samples: Orientation[] = [];
        if (latestRef.current) samples.push(latestRef.current);

        const collector = (o: Orientation) => samples.push(o);
        collectorsRef.current.add(collector);

        setTimeout(() => {
          collectorsRef.current.delete(collector);
          if (samples.length === 0) {
            reject(new Error('No sensor samples received'));
            return;
          }
          // Mean of the quaternions, then re-derive Euler from the result.
          // For display we keep the LATEST raw alpha/beta/gamma since the user
          // mostly cares about the quaternion, but average them too.
          const meanQ = quatMean(samples.map((s) => s.q));
          const meanA =
            avgAngleCircular(samples.map((s) => s.alpha));
          const meanB =
            samples.reduce((sum, s) => sum + s.beta, 0) / samples.length;
          const meanG =
            samples.reduce((sum, s) => sum + s.gamma, 0) / samples.length;
          resolve({
            alpha: meanA,
            beta: meanB,
            gamma: meanG,
            q: meanQ,
            t: performance.now(),
          });
        }, durationMs);
      });
    },
    [permission],
  );

  return {
    orientation,
    permission,
    request,
    captureSample,
  };
}

/**
 * Mean of angles that wrap at 360° (used for alpha). Converts each angle
 * to a unit vector on the circle, averages the vectors, then back to angle.
 */
function avgAngleCircular(angles: number[]): number {
  if (angles.length === 0) return 0;
  let sx = 0, sy = 0;
  for (const a of angles) {
    const r = (a * Math.PI) / 180;
    sx += Math.cos(r);
    sy += Math.sin(r);
  }
  const meanRad = Math.atan2(sy / angles.length, sx / angles.length);
  let deg = (meanRad * 180) / Math.PI;
  if (deg < 0) deg += 360;
  return deg;
}
